// The setup screen's look: the configuration prototype's layout
// (docs/design/user-defined-attributes/configuration.html) on the shared
// theme tokens. One scoped stylesheet (class names prefixed `uds-`) so the
// markup reads like the prototype and every host renders it the same; buttons
// are the app's own `.btn` classes (filled `.btn-secondary`, untouched
// `.btn-primary`). Customize-owned marks use the mode's violet.

import type { CSSProperties, ReactNode } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { VIOLET, VIOLET_LINE, VIOLET_SOFT } from '../../customize/ui'
import { tw } from '../../testHooks'

/** One colour per attribute set (`set.color`, cycled) — theme tokens only. */
export const SET_COLOR_TOKENS: readonly string[] = [
  'var(--color-accent)', 'var(--color-success)', 'var(--color-warning)', 'var(--color-purple)',
  'var(--color-info)', 'var(--color-error)', 'var(--color-orange)', 'var(--color-skyblue)',
]

export function setColor(color: number | null | undefined): string {
  const n = Number(color)
  const i = Number.isFinite(n) && n >= 0 ? Math.floor(n) % SET_COLOR_TOKENS.length : 0
  return SET_COLOR_TOKENS[i]
}

export function SetDot({ color, size = 10 }: { color: number | null | undefined; size?: number }) {
  return <span aria-hidden className="uds-sdot" style={{ width: size, height: size, background: setColor(color) }} />
}

export const FONT = "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif"
export const MONO = "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace"

export const SETUP_CSS = `
.uds-root{display:flex;flex-direction:column;height:100%;min-height:0;background:var(--color-bg-secondary);color:var(--color-text);font-family:${FONT};font-size:14px;line-height:1.5}
.uds-root *{box-sizing:border-box}
.uds-root button,.uds-dlg button,.uds-root input,.uds-root select,.uds-root textarea,.uds-dlg input,.uds-dlg select,.uds-dlg textarea{font:inherit;color:inherit}
.uds-mono{font-family:${MONO};font-size:13px}
.uds-grow{flex:1}
.uds-top{background:var(--color-bg);border-bottom:1px solid var(--color-border);display:flex;align-items:center;gap:10px 14px;min-height:54px;flex-wrap:wrap;padding:6px 16px}
.uds-top h1{margin:0;font-size:16px;font-weight:600;display:flex;align-items:center;gap:8px}
.uds-crumbs{color:var(--color-text-secondary);font-size:13px}
.uds-crumbs b{color:var(--color-text)}
.uds-state{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:var(--color-warning)}
.uds-state.live{color:var(--color-success)}
.uds-state .dot{width:8px;height:8px;border-radius:50%;background:currentColor}
.uds-steps{background:var(--color-bg);border-bottom:1px solid var(--color-border);overflow-x:auto}
.uds-steps-in{display:flex;align-items:center;gap:6px;min-height:46px;padding:6px 16px}
.uds-step{display:inline-flex;align-items:center;gap:8px;border:1px solid transparent;background:transparent;border-radius:999px;padding:5px 12px 5px 6px;font-size:13px;font-weight:600;color:var(--color-text-secondary);white-space:nowrap;cursor:pointer}
.uds-step .n{width:22px;height:22px;border-radius:50%;display:grid;place-items:center;font-size:13px;font-weight:700;background:var(--color-bg-secondary);color:var(--color-text-secondary);border:1px solid var(--color-border-strong)}
.uds-step.done .n{background:var(--color-success-weak);color:var(--color-success);border-color:transparent}
.uds-step.on{background:var(--color-accent-weak);color:var(--color-accent)}
.uds-step.on .n{background:var(--color-accent);color:#fff;border-color:var(--color-accent)}
.uds-step .c{font-weight:500;color:var(--color-text-faint)}
.uds-sep{color:var(--color-text-faint);flex:none;display:inline-flex}
.uds-ws{flex:1;min-height:0;display:grid;grid-template-columns:272px minmax(0,1fr) 384px}
.uds-tree{background:var(--color-bg);border-right:1px solid var(--color-border);overflow:auto;padding:10px 8px 24px;min-height:0}
.uds-tn{display:flex;align-items:center;gap:7px;width:100%;border:0;background:transparent;text-align:left;padding:5px 8px;border-radius:6px;font-size:14px;color:var(--color-text);min-height:30px;cursor:pointer}
.uds-tn:hover{background:var(--color-bg-secondary)}
.uds-tn.on{background:var(--color-accent-weak);color:var(--color-accent);font-weight:600}
.uds-tn .m{margin-left:auto;font-size:13px;color:var(--color-text-faint);font-weight:500;white-space:nowrap}
.uds-tn.on .m{color:var(--color-accent)}
.uds-tn.l1{padding-left:22px}.uds-tn.l2{padding-left:38px;font-size:13px}.uds-tn.l3{padding-left:56px;font-size:13px}
.uds-tn .t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
.uds-work{min-width:0;min-height:0;overflow:auto;padding:18px 20px 60px;display:grid;gap:16px;align-content:start}
.uds-props{background:var(--color-bg);border-left:1px solid var(--color-border);overflow:auto;min-height:0}
.uds-props-h{padding:12px 14px;border-bottom:1px solid var(--color-border);display:grid;gap:4px}
.uds-props-h .row{display:flex;gap:6px;align-items:center;flex-wrap:wrap}
.uds-props-h h2{margin:0;font-size:15px;font-weight:600}
.uds-props-h .sub{font-size:13px;color:var(--color-text-faint);font-family:${MONO}}
.uds-ptabs{display:flex;border-bottom:1px solid var(--color-border);overflow-x:auto;padding:0 6px}
.uds-ptab{border:0;background:transparent;padding:9px 10px;font-size:13px;font-weight:500;color:var(--color-text-secondary);border-bottom:2px solid transparent;white-space:nowrap;display:inline-flex;gap:5px;align-items:center;cursor:pointer}
.uds-ptab.on{color:var(--color-accent);border-bottom-color:var(--color-accent);font-weight:600}
.uds-ptab .c{font-size:13px;font-weight:700;background:var(--color-accent);color:#fff;border-radius:999px;padding:0 6px;line-height:18px}
.uds-pbody{padding:12px 14px 24px}
.uds-inp{width:100%;min-height:36px;padding:7px 10px;border-radius:6px;border:1px solid var(--color-border-strong);background:var(--color-input-bg);color:var(--color-text)}
.uds-inp::placeholder{color:var(--color-text-faint)}
.uds-inp:disabled{background:var(--color-bg-secondary);color:var(--color-text-secondary);cursor:not-allowed}
.uds-inp.sm{min-height:32px;padding:4px 8px;font-size:13px}
.uds-inp.mono{font-family:${MONO};font-size:13px}
textarea.uds-inp{resize:vertical}
.uds-lbl{display:block;font-size:13px;font-weight:600;margin:12px 0 5px}
.uds-lbl:first-child{margin-top:0}
.uds-hint{font-size:13px;color:var(--color-text-faint);margin-top:5px}
.uds-hintbox{display:flex;gap:8px;align-items:flex-start;font-size:13px;color:var(--color-text-secondary);background:var(--color-bg-secondary);border-radius:6px;padding:8px 10px;margin-top:8px}
.uds-hintbox svg{margin-top:2px;color:var(--color-accent);flex:none}
.uds-pill{display:inline-flex;align-items:center;gap:4px;font-size:13px;font-weight:600;border-radius:4px;padding:1px 7px;white-space:nowrap}
.uds-p-std{background:var(--color-bg-secondary);color:var(--color-text-secondary);border:1px solid var(--color-border)}
.uds-p-ours{background:${VIOLET_SOFT};color:${VIOLET}}
.uds-p-draft{background:var(--color-warning-weak);color:var(--color-warning)}
.uds-p-live{background:var(--color-success-weak);color:var(--color-success)}
.uds-p-acc{background:var(--color-accent-weak);color:var(--color-accent)}
.uds-p-cz{background:${VIOLET_SOFT};color:${VIOLET};border:1px solid ${VIOLET_LINE}}
.uds-vchip{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--color-border-strong);background:var(--color-input-bg);border-radius:999px;padding:2px 10px;font-size:13px;font-weight:500;min-height:30px;cursor:pointer}
.uds-vchip.on{background:var(--color-accent-weak);border-color:var(--color-accent);color:var(--color-accent)}
.uds-vchip .cnt{font-size:13px;color:var(--color-text-faint);font-weight:500}
.uds-vchip.on .cnt{color:var(--color-accent)}
.uds-vchip:disabled{opacity:.55;cursor:not-allowed}
.uds-chips{display:flex;flex-wrap:wrap;gap:6px}
.uds-seg{display:inline-flex;border:1px solid var(--color-border-strong);border-radius:6px;overflow:hidden;flex-wrap:wrap;background:var(--color-input-bg)}
.uds-seg button{border:0;background:transparent;padding:5px 11px;min-height:32px;font-size:13px;font-weight:500;color:var(--color-text-secondary);border-right:1px solid var(--color-border);cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.uds-seg button:last-child{border-right:0}
.uds-seg button.on{background:var(--color-accent-weak);color:var(--color-accent);font-weight:600}
.uds-seg button:disabled{opacity:.55;cursor:not-allowed}
.uds-statebox{display:flex;align-items:center;gap:8px;font-size:14px;border:1px solid var(--color-border);border-radius:6px;padding:7px 10px;cursor:pointer}
.uds-statebox.on{border-color:var(--color-accent);background:var(--color-accent-weak)}
.uds-statebox.off{cursor:not-allowed;opacity:.65}
.uds-statebox input{width:16px;height:16px;accent-color:var(--color-accent)}
.uds-grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px}
.uds-setup{border:1px solid var(--color-border);border-radius:8px;overflow:hidden}
.uds-setup button{display:flex;align-items:center;gap:10px;width:100%;text-align:left;padding:8px 12px;background:transparent;border:0;border-top:1px solid var(--color-border);cursor:pointer}
.uds-setup button:first-child{border-top:0}
.uds-setup button:hover{background:var(--color-bg-secondary)}
.uds-setup .d{width:8px;height:8px;border-radius:50%;border:1.5px solid var(--color-text-faint);flex:none}
.uds-setup .d.on{background:var(--color-accent);border-color:var(--color-accent)}
.uds-setup .t{font-weight:500;display:block}
.uds-setup .s{font-size:13px;color:var(--color-text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block;max-width:220px}
.uds-setup .go{margin-left:auto;font-size:13px;color:var(--color-accent)}
.uds-pcard{border:1px solid var(--color-border);border-radius:8px;margin-top:14px}
.uds-pcard-h{display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border);font-weight:600;font-size:13px;border-radius:8px 8px 0 0}
.uds-pcard-b{padding:10px 12px 12px}
.uds-vrow{display:flex;gap:6px;align-items:center;margin-top:6px}
.uds-vrow .uds-inp{min-width:0}
.uds-li{display:flex;align-items:center;gap:8px;border:1px solid var(--color-border);border-radius:6px;padding:6px 8px;margin-top:6px;font-size:13px}
.uds-li .t{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.uds-li.off .t{opacity:.55}
.uds-li input[type=checkbox]{width:16px;height:16px;accent-color:var(--color-accent)}
.uds-card{background:var(--color-bg);border:1px solid var(--color-border);border-radius:8px}
.uds-card-h{display:flex;align-items:center;gap:8px 12px;padding:12px 14px;border-bottom:1px solid var(--color-border);flex-wrap:wrap}
.uds-card-h h2,.uds-card-h h3{margin:0;font-size:15px;font-weight:600}
.uds-card-h p{margin:0;font-size:13px;color:var(--color-text-secondary);flex-basis:100%}
.uds-card-b{padding:14px}
.uds-lead h2{margin:0 0 4px;font-size:19px;font-weight:600}
.uds-lead p{margin:0;color:var(--color-text-secondary);max-width:75ch}
.uds-tblwrap{overflow-x:auto;max-width:100%}
.uds-tbl{width:100%;border-collapse:collapse;font-size:14px}
.uds-tbl th{text-align:left;font-size:13px;font-weight:600;background:var(--color-bg-secondary);color:var(--color-text-secondary);padding:8px 10px;border-bottom:1px solid var(--color-border);white-space:nowrap}
.uds-tbl td{padding:8px 10px;border-bottom:1px solid var(--color-border);vertical-align:middle}
.uds-tbl tr.click{cursor:pointer}
.uds-tbl tr.click:hover td{background:var(--color-bg-secondary)}
.uds-tbl tr.sel td{background:var(--color-accent-weak)}
.uds-tbl .c{text-align:center}
.uds-tbl .r{text-align:right;font-variant-numeric:tabular-nums}
.uds-tbl tbody.setgrp{border-top:2px solid var(--color-border)}
.uds-tbl .setcell{min-width:200px}
.uds-tbl tr.or td{border-top:0}
.uds-ortag{font-size:13px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--color-text-faint);width:44px}
.uds-thsub{font-weight:400;font-family:${MONO};font-size:13px;color:var(--color-text-faint)}
.uds-icons{display:inline-flex;gap:5px;color:var(--color-accent)}
.uds-na{color:var(--color-text-faint);font-style:italic;font-size:13px}
.uds-formcards{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:12px}
.uds-fc{border:1px solid var(--color-border-strong);background:var(--color-bg);border-radius:10px;padding:14px;text-align:left;display:grid;gap:6px;cursor:pointer}
.uds-fc.on{border-color:var(--color-accent);box-shadow:0 0 0 3px var(--color-accent-weak)}
.uds-fc b{font-size:15px}
.uds-fc .s{font-size:13px;color:var(--color-text-secondary)}
.uds-picker{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px}
.uds-src{border:1px solid var(--color-border);border-radius:8px;overflow:hidden;background:var(--color-bg)}
.uds-src-h{padding:8px 12px;background:var(--color-bg-secondary);font-size:13px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--color-text-faint);border-bottom:1px solid var(--color-border)}
.uds-srow{display:flex;align-items:center;gap:8px;padding:7px 12px;border-bottom:1px solid var(--color-border);font-size:14px;width:100%;background:transparent;border-left:0;border-right:0;border-top:0;text-align:left}
.uds-srow:last-child{border-bottom:0}
.uds-srow.sub{padding-left:34px;font-size:13px;cursor:pointer}
.uds-srow.head{cursor:pointer}
.uds-srow .m{margin-left:auto;font-size:13px;color:var(--color-text-faint);white-space:nowrap}
.uds-srow input[type=checkbox]{width:16px;height:16px;accent-color:var(--color-accent)}
.uds-srow.disabled{color:var(--color-text-faint);cursor:default}
.uds-decider{display:flex;align-items:center;gap:8px;border:1px solid var(--color-border);border-radius:8px;padding:8px 10px;margin-bottom:6px;background:var(--color-bg)}
.uds-decider.on{border-color:var(--color-accent);background:var(--color-accent-weak)}
.uds-decider .t{font-weight:600}
.uds-decider .p{font-family:${MONO};font-size:13px;color:var(--color-text-faint);display:block}
.uds-banner{display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border-radius:8px;font-size:14px;flex-wrap:wrap}
.uds-banner.w{background:var(--color-warning-weak);color:var(--color-text)}
.uds-banner.e{background:var(--color-error-weak);color:var(--color-text)}
.uds-banner.i{background:var(--color-accent-weak);color:var(--color-text)}
.uds-banner svg{margin-top:2px;flex:none}
.uds-banner ul{margin:4px 0 0;padding-left:18px}
.uds-spin{width:14px;height:14px;border:2px solid var(--color-border-strong);border-top-color:var(--color-accent);border-radius:50%;animation:uds-spin .8s linear infinite;display:inline-block}
@keyframes uds-spin{to{transform:rotate(360deg)}}
.uds-icbtn{display:inline-grid;place-items:center;width:28px;height:28px;border-radius:6px;border:0;background:transparent;color:var(--color-text-secondary);flex:none;padding:0;cursor:pointer}
.uds-icbtn:hover{background:var(--color-bg-secondary)}
.uds-icbtn:disabled{opacity:.4;cursor:not-allowed}
.uds-danger{color:var(--color-error)!important}
.uds-covbar{display:flex;gap:10px 22px;align-items:center;flex-wrap:wrap;background:var(--color-bg);border:1px solid var(--color-border);border-radius:8px;padding:10px 14px}
.uds-covnum{display:flex;gap:6px;align-items:baseline}
.uds-covnum b{font-size:20px;font-variant-numeric:tabular-nums}
.uds-covnum span{font-size:13px;color:var(--color-text-secondary)}
.uds-covnum.warn b{color:var(--color-warning)}
.uds-sdot{border-radius:3px;flex:none;display:inline-block}
.uds-pick{display:inline-flex;align-items:center;gap:6px;min-height:32px;max-width:240px;padding:4px 8px 4px 10px;border-radius:6px;border:1px solid var(--color-border-strong);background:var(--color-input-bg);font-size:13px;text-align:left;cursor:pointer}
.uds-pick .pl{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.uds-pick .pc{font-size:13px;font-weight:700;background:var(--color-accent-weak);color:var(--color-accent);border-radius:999px;padding:0 6px}
.uds-pick.any{border-style:dashed;color:var(--color-text-secondary)}
.uds-pick:disabled{cursor:not-allowed;opacity:.7}
.uds-pklist{border:1px solid var(--color-border);border-radius:8px;max-height:340px;overflow:auto;background:var(--color-bg)}
.uds-pkrow{display:flex;align-items:center;gap:10px;padding:7px 12px;border-bottom:1px solid var(--color-border);font-size:14px;cursor:pointer}
.uds-pkrow:last-child{border-bottom:0}
.uds-pkrow:hover{background:var(--color-bg-secondary)}
.uds-pkrow input{width:16px;height:16px;accent-color:var(--color-accent)}
.uds-bar{display:flex;gap:8px 10px;align-items:center;flex-wrap:wrap;margin-bottom:10px}
.uds-gridwrap{overflow:auto;max-height:560px;border:1px solid var(--color-border);border-radius:8px;background:var(--color-bg)}
.uds-agrid{border-collapse:separate;border-spacing:0;font-size:13px}
.uds-agrid th{position:sticky;top:0;background:var(--color-bg-secondary);z-index:2;border-bottom:1px solid var(--color-border);padding:0;text-align:left;font-weight:600}
.uds-agrid tbody th{position:sticky;left:0;z-index:1;background:var(--color-bg);border-right:1px solid var(--color-border);border-bottom:1px solid var(--color-border)}
.uds-agrid th.corner{left:0;z-index:3;padding:8px 10px;color:var(--color-text-secondary);white-space:nowrap}
.uds-agrid td{padding:3px;border-bottom:1px solid var(--color-border);border-right:1px solid var(--color-border)}
.uds-ghead{display:flex;flex-direction:column;align-items:flex-start;gap:0;border:0;background:transparent;padding:6px 10px;width:100%;min-width:118px;text-align:left;font-weight:600;font-size:13px;cursor:pointer}
.uds-ghead.row{flex-direction:row;justify-content:space-between;gap:10px;min-width:170px}
.uds-ghead:not([disabled]):hover{background:var(--color-accent-weak);color:var(--color-accent)}
.uds-ghead[disabled]{cursor:default;color:inherit}
.uds-gn{font-size:13px;font-weight:500;color:var(--color-text-faint)}
.uds-gcell{display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:118px;min-height:46px;border:1px solid transparent;border-radius:6px;background:transparent;padding:4px 6px;text-align:left;user-select:none;cursor:pointer}
.uds-gcell:hover{border-color:var(--color-border-strong);background:var(--color-bg-secondary)}
.uds-gcell.empty{background:var(--color-warning-weak)}
.uds-gcell.mine{border-color:var(--color-accent);box-shadow:inset 0 0 0 1px var(--color-accent)}
.uds-gb{display:flex;gap:5px;align-items:center;font-size:13px;line-height:1.3;max-width:100%}
.uds-gb span:last-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.uds-gmore{font-size:13px;color:var(--color-text-faint);font-weight:600}
.uds-gnone{font-size:13px;color:var(--color-warning);font-weight:600}
.uds-rgrp{background:var(--color-bg);border:1px solid var(--color-border);border-radius:8px}
.uds-rgrp-h{display:flex;align-items:center;gap:8px;padding:9px 14px;border-bottom:1px solid var(--color-border);background:var(--color-bg-secondary);border-radius:8px 8px 0 0;flex-wrap:wrap}
.uds-rgrp-h h3{margin:0;font-size:14px;font-weight:600}
.uds-rgrp-h .why{font-size:13px;color:var(--color-text-secondary)}
.uds-chg{display:flex;gap:10px;align-items:flex-start;padding:7px 0;border-bottom:1px solid var(--color-border);font-size:14px}
.uds-chg:last-child{border-bottom:0}
.uds-chg svg{margin-top:3px;color:var(--color-accent);flex:none}
.uds-sect{font-size:13px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--color-text-faint)}
.uds-code{font-family:${MONO};font-size:13px;background:var(--color-bg-secondary)}
.uds-toast{position:fixed;left:50%;bottom:20px;transform:translateX(-50%);background:var(--color-text);color:var(--color-bg);padding:10px 16px;border-radius:8px;font-size:14px;font-weight:500;box-shadow:0 10px 30px rgba(16,24,40,.18);z-index:100001;max-width:calc(100% - 32px)}
.uds-toast.error{background:var(--color-error);color:#fff}
@media (max-width:1180px){.uds-ws{grid-template-columns:232px minmax(0,1fr) 352px}}
@media (max-width:980px){
  .uds-ws{grid-template-columns:minmax(0,1fr) 352px;grid-template-rows:auto minmax(0,1fr)}
  .uds-tree{grid-column:1/-1;border-right:0;border-bottom:1px solid var(--color-border);display:flex;gap:4px;overflow-x:auto;padding:8px 16px}
  .uds-tree .uds-tn{width:auto;white-space:nowrap;padding-left:10px!important}
  .uds-tree .uds-tn.l3{display:none}
}
@media (max-width:760px){
  .uds-ws{grid-template-columns:minmax(0,1fr);display:block;overflow:auto}
  .uds-props{border-left:0;border-top:1px solid var(--color-border)}
  .uds-work{padding:14px 16px 24px;overflow:visible}
  .uds-picker{grid-template-columns:minmax(0,1fr)}
}
@media (prefers-reduced-motion: reduce){.uds-spin{animation:none}}
`

export function SetupStyles() {
  return <style>{SETUP_CSS}</style>
}

export function HintBox({ icon, children, style }: { icon?: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="uds-hintbox" style={style}>
      {icon ?? <Info size={15} />}
      <span>{children}</span>
    </div>
  )
}

export function Banner({ tone = 'w', children, action }: { tone?: 'w' | 'e' | 'i'; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`uds-banner ${tone}`} role={tone === 'e' ? 'alert' : 'status'}>
      {tone === 'i' ? <Info size={16} style={{ color: 'var(--color-accent)' }} />
        : <AlertTriangle size={16} style={{ color: tone === 'e' ? 'var(--color-error)' : 'var(--color-warning)' }} />}
      <div className="uds-grow" style={{ minWidth: 0 }}>{children}</div>
      {action}
    </div>
  )
}

export function Seg<T extends string>({ value, options, onChange, disabled, ariaLabel }: {
  value: T
  options: Array<{ value: T; label: ReactNode; title?: string; hook?: string }>
  onChange: (v: T) => void
  disabled?: boolean
  ariaLabel?: string
}) {
  return (
    <div className="uds-seg" role="tablist" aria-label={ariaLabel}>
      {options.map(o => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value}
          className={value === o.value ? 'on' : ''} disabled={disabled} title={o.title}
          onClick={() => onChange(o.value)} {...tw('action', o.hook)}>{o.label}</button>
      ))}
    </div>
  )
}

export function StateBox({ checked, onChange, children, disabled, title }: {
  checked: boolean
  onChange: (v: boolean) => void
  children: ReactNode
  disabled?: boolean
  title?: string
}) {
  return (
    <label className={`uds-statebox ${checked ? 'on' : ''} ${disabled ? 'off' : ''}`} title={title}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      {children}
    </label>
  )
}

/** "Standard" / "Ours" plus "Published" / "Draft" pills. */
export function OriginPills({ standard, published }: { standard: boolean; published?: boolean | null }) {
  return (
    <>
      {standard ? <span className="uds-pill uds-p-std">Standard</span> : <span className="uds-pill uds-p-ours">Ours</span>}
      {published === true && <span className="uds-pill uds-p-live">Published</span>}
      {published === false && <span className="uds-pill uds-p-draft">Draft</span>}
    </>
  )
}
